/**
 * Ritual Organizers Hook
 * Fetches ritual organizers with pagination and infinite scroll support
 */

import { useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { getRitualOrganizers, type RitualOrganizerItem } from "../services/ritual.service"

interface UseRitualOrganizersOptions {
  ritualId: string
  limit?: number
}

interface UseRitualOrganizersResult {
  organizers: RitualOrganizerItem[]
  pagination: {
    page: number
    limit: number
    totalItems: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  } | null
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  isError: boolean
  error: Error | null
  refetch: () => void
}

export function useRitualOrganizers({
  ritualId,
  limit = 20,
}: UseRitualOrganizersOptions): UseRitualOrganizersResult {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["ritual-organizers", ritualId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getRitualOrganizers(ritualId, pageParam, limit)
      return response
    },
    getNextPageParam: lastPage => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    initialPageParam: 1,
  })

  // Flatten pages to get all organizers (memoized to prevent infinite re-renders in consumers)
  const organizers = useMemo(() => data?.pages.flatMap(page => page.items) || [], [data?.pages])
  const pagination = data?.pages[0]?.pagination || null

  return {
    organizers,
    pagination,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isError,
    error: error as Error | null,
    refetch,
  }
}
